import { useRequest } from 'features/request/useRequest';
import type { BimPriceLevelEnum } from 'gql/graphql';
import { WorkOrderPriceRevisionTypeEnum } from 'gql/graphql';
import { useAgGridDataSource } from 'hooks/useAgGridDataSource';
import { bimWorkOrderVersionPriceRevisionsGql } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/bimWorkOrderPriceQueries';
import { getWhereVariables } from 'pages/Project/BimProject/WorkOrder/BimWorkOrderPriceTable/hooks/helpers';
import { BimWorkOrderRowDetailsType } from 'pages/Project/BimProject/WorkOrder/types';

import { useEffect, useMemo } from 'react';

const requestId = 'bimWorkOrderPriceVersionRevisions';

export const useBimWorkOrderPriceVersionRevisions = (
    workOrderVersionPriceId: number,
    detailsType: BimWorkOrderRowDetailsType,
    filterValues: Record<string, string[]>,
    priceLevel: BimPriceLevelEnum | undefined,
) => {
    const revisionType = useMemo<WorkOrderPriceRevisionTypeEnum>(
        () =>
            detailsType === BimWorkOrderRowDetailsType.Revision
                ? WorkOrderPriceRevisionTypeEnum.Revision
                : WorkOrderPriceRevisionTypeEnum.Update,
        [detailsType],
    );

    const variables = useMemo(
        () => ({
            workOrderVersionPriceId,
            revisionType,
            priceLevel,
            where: getWhereVariables(filterValues),
        }),
        [filterValues, priceLevel, revisionType, workOrderVersionPriceId],
    );

    const [bimWorkOrderPriceVersionRevisionsData, , , bimWorkOrderPriceVersionRevisionsRequest] = useRequest({
        requestId,
        query: bimWorkOrderVersionPriceRevisionsGql,
        withEffect: false,
    });

    const { dataSource, getRowParams } = useAgGridDataSource({
        requestId,
        requestFn: bimWorkOrderPriceVersionRevisionsRequest,
        requestVariables: variables,
    });

    useEffect(() => {
        if (bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.items && getRowParams?.current) {
            getRowParams?.current?.successCallback(
                bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.items,
                bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.totalCount,
            );
        }
    }, [
        bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.items,
        bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.totalCount,
        getRowParams,
    ]);

    const bimWorkOrderPriceRevisionsItemsCount = useMemo(
        () => bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.totalCount ?? 0,
        [bimWorkOrderPriceVersionRevisionsData?.bimWorkOrderVersionPriceRevisions?.totalCount],
    );

    const bimWorkOrderPriceRevisionsFetchedItemsCounter = useMemo(() => {
        if (!getRowParams?.current?.endRow || !bimWorkOrderPriceRevisionsItemsCount) {
            return 0;
        }

        if (getRowParams?.current?.endRow > bimWorkOrderPriceRevisionsItemsCount) {
            return bimWorkOrderPriceRevisionsItemsCount;
        }

        return getRowParams?.current?.endRow;
        // eslint-disable-next-line
    }, [bimWorkOrderPriceRevisionsItemsCount, getRowParams?.current?.endRow]);

    return {
        bimWorkOrderPriceVersionRevisionsDataSource: dataSource,
        bimWorkOrderPriceRevisionsItemsCount,
        bimWorkOrderPriceRevisionsFetchedItemsCounter,
    };
};
